// TagMenu.tsx — ONE control where the fleet has two circles (Stash had FOUR in a row).
//
// 🔴 IDENTITY (2026-08-30): "wordmark-left + circular-controls-right" is one of the four banned
// fleet fingerprints, and this app carried the loudest version of it: an italic "Stash" wordmark
// beside four round buttons. The wordmark is gone (the next-out season is the header now) and
// backup + account collapse here into a single control drawn in this app's own material — a
// tied-on shipping LABEL: square with the top-right corner cut, the way a tag is. Deliberately
// not a third circle, and deliberately not any sibling's shape.
//
// The shared BackupMenu / AccountMenu are rendered UNCHANGED inside — the account-deletion flow
// the store gate depends on lives inside AccountMenu and must not be reimplemented here.

import { useState, type ReactNode } from "react";
import { platform } from "./tauri-web";
import { WalletTopUp } from "./iap";

export function TagMenu({ backup, account }: { backup: ReactNode; account: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Apple 2.1(b) (Focus/Gifted rejections, 2026-09-05): the wallet top-up must be visible
  // the moment this drawer opens, not nested one tap deeper inside AccountMenu's own avatar
  // popover. Rendered directly here, gated iOS/MAS same as AccountMenu's own gate.
  const isIos = platform() === "ios";
  const isMasMac = platform() === "macos" && (import.meta as any).env?.VITE_MAS === "1";
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="stc-tagmenu"
        aria-label="Settings, backup and account"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 3.7v2.2M12 18.1v2.2M3.7 12h2.2M18.1 12h2.2M6.2 6.2l1.5 1.5M16.3 16.3l1.5 1.5M17.8 6.2l-1.5 1.5M7.7 16.3l-1.5 1.5"
                stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="stc-menu-panel">
            {(isIos || isMasMac) && (
              <>
                <div style={{ padding: "0.5rem 0.5rem 0.15rem" }}><WalletTopUp /></div>
                <div className="stc-menu-rule" />
              </>
            )}
            <div className="stc-menu-row">{backup}<span>Back up &amp; restore</span></div>
            <div className="stc-menu-rule" />
            <div className="stc-menu-row">{account}<span>Account</span></div>
          </div>
        </>
      )}
    </div>
  );
}

// 🔴 THE ASSISTANT ENTRY — a stencilled crate label on the next-out rail, replacing the floating
// "Chat" pill ~15 siblings shared. onAsk opens the same Chat sheet: moved, never removed.
export function AskCrate({ onAsk }: { onAsk: () => void }) {
  return (
    <button type="button" className="stc-crate" onClick={onAsk} aria-label="Open chat assistant">
      <span className="stc-crate-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none">
          <path d="M3.4 7.6 12 3.6l8.6 4v8.8L12 20.4 3.4 16.4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M3.4 7.6 12 11.6l8.6-4M12 11.6v8.8" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </span>
      <span className="stc-crate-txt">
        <span className="stc-crate-lbl">Ask</span>
        <span className="stc-crate-sub">find · file · recap</span>
      </span>
    </button>
  );
}
